import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase, isSupabaseConfigured } from '@/lib/supabase'

interface AuthContextValue {
  user: User | null
  session: Session | null
  isLoading: boolean
  isConfigured: boolean
  signUp: (
    email: string,
    password: string,
    name: string,
    captchaToken: string
  ) => Promise<{ error: AuthError | null }>
  signIn: (
    email: string,
    password: string,
    captchaToken: string
  ) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isConfigured = isSupabaseConfigured()

  useEffect(() => {
    if (!isConfigured) {
      // Defer state update to avoid sync setState warning
      const timer = setTimeout(() => setIsLoading(false), 0)
      return () => clearTimeout(timer)
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setIsLoading(false)
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      setUser(session?.user ?? null)
    })

    return () => subscription.unsubscribe()
  }, [isConfigured])

  const signUp = useCallback(
    async (email: string, password: string, name: string, captchaToken: string) => {
      if (!isConfigured) {
        return { error: { message: 'Supabase is not configured' } as AuthError }
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
          },
          captchaToken,
        },
      })

      return { error }
    },
    [isConfigured]
  )

  const signIn = useCallback(
    async (email: string, password: string, captchaToken: string) => {
      if (!isConfigured) {
        return { error: { message: 'Supabase is not configured' } as AuthError }
      }

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          captchaToken,
        },
      })

      return { error }
    },
    [isConfigured]
  )

  const signOut = useCallback(async () => {
    if (!isConfigured) return
    await supabase.auth.signOut()
  }, [isConfigured])

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        isConfigured,
        signUp,
        signIn,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
